//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  PageFirst,
  PageLast,
} from '@carbon/icons-react';
import { cn } from '../../lib/utils';
import { Search } from 'lucide-react';
import LoopDataService from '../../utils/LoopDataService';
import TenantDataService from '../../utils/TenantDataService';
import AiDataService from '../../utils/AiDataService';
import { useUserAuth } from '../../UserAuth';
import { useTeamspaceContext } from '../../TeamspaceContext';
import ReactMarkdown from 'react-markdown';
import AiSummaryIcon from '../../icons/AiSummaryIcon.svg';
import { useTranslation } from 'react-i18next';
import { useLoopContext } from '../../LoopContext';
import FilterCards, { UnifiedCardData } from './filterCards';
import { useSearchContext } from '../../components/SearchContext';

interface SearchResult {
  issueId: string;
  issueName: string;
  issueDescription: string;
  issueOwner: string;
  issueUrl: string;
  issueCreated: number;
  issueType: string;
  project?: string | null;
  commitUrls?: string[];
  pullRequestUrls?: string[];
  buildUrls?: string[];
  issueStatus?: string;
  issuePriority?: string | null;
  issueStoryPoints?: number | null;
  issueResolution?: string | null;
  sprints?: any;
  releases?: any;
  issueCreator?: string;
  issueLastUpdate?: number;
}

const transformSearchResults = (
  hits: any[],
  category: string,
): UnifiedCardData[] => {
  switch (category) {
    case 'Issues':
      return hits.map((hit) => ({
        id: hit.issueId,
        type: 'Issues' as const,
        cardTitle: hit.issueName,
        created: hit.issueCreated,
        owner: hit.issueOwner,
        loopName: hit.loopName,
        description: hit.issueDescription || '',
        url: hit.issueUrl,
        status: hit.issueStatus,
        issueType: hit.issueType,
        commitUrls: Array.isArray(hit.commitUrls) ? hit.commitUrls : [],
        pullRequestUrls: Array.isArray(hit.pullRequestUrls)
          ? hit.pullRequestUrls
          : [],
        buildUrls: Array.isArray(hit.buildUrls) ? hit.buildUrls : [],
      }));

    case 'Builds':
      return hits.map((hit) => ({
        id: hit.buildId,
        type: 'Builds' as const,
        cardTitle: hit.buildName || '',
        created: hit.buildStartTime,
        owner: hit.buildRequestor || '',
        loopName: hit.loopName || '',
        application: hit.applicationName || '',
        description: hit.buildName || hit.buildId || '',
        url: hit.buildUrl,
        status: hit.buildStatus || '',
        buildBranch: (hit.buildBranch === null ? '' : hit.buildBranch) || '',
        buildLabel: (hit.buildLabel === null ? '' : hit.buildLabel) || '',
        buildSource: hit.buildSource || '',
        commits:
          hit.commitsCount != null
            ? String(hit.commitsCount)
            : Array.isArray(hit.commitUrls)
              ? String(hit.commitUrls.length)
              : '',
        sourceBranch: hit.buildSource || '',
        branchName: (hit.buildBranch === null ? '' : hit.buildBranch) || '',
        labelsList: (hit.buildLabel === null ? '' : hit.buildLabel) || '',
        commitUrls: Array.isArray(hit.commitUrls) ? hit.commitUrls : [],
        pullRequestUrls: Array.isArray(hit.pullRequestUrls)
          ? hit.pullRequestUrls
          : [],
        buildUrls: Array.isArray(hit.buildUrls) ? hit.buildUrls : [],
      }));

    case 'Pull Requests':
      return hits.map((hit) => ({
        id: hit.pullRequestId,
        type: 'Pull Requests' as const,
        cardTitle: hit.pullRequestName || '',
        created: hit.pullRequestCreated,
        owner: hit.pullRequestCreator || '',
        loopName: hit.loopName || '',
        description: hit.pullRequestDescription || hit.description || '',
        url: hit.pullRequestUrl || hit.url,
        status: hit.pullRequestStatus || '',
        baseBranch: hit.pullRequestBaseBranch,
        repositoryName: hit.pullRequestRepositoryName,
        repositoryOwner: hit.pullRequestRepositoryOwner,
        commitUrls: Array.isArray(hit.commitUrls) ? hit.commitUrls : [],
        pullRequestUrls: Array.isArray(hit.pullRequestUrls)
          ? hit.pullRequestUrls
          : [],
        buildUrls: Array.isArray(hit.buildUrls) ? hit.buildUrls : [],
      }));

    case 'Commits':
      return hits.map((hit) => ({
        id: hit.commitId || hit.sha,
        type: 'Commits' as const,
        cardTitle: hit.commitName || hit.commitMessage || hit.message || '',
        created: hit.commitCreated || hit.commitTimestamp || hit.timestamp,
        owner: hit.commitCommitter || hit.commitAuthor || hit.author || '',
        loopName: hit.loopName || '',
        description: hit.commitMessage || hit.message || '',
        url: hit.commitUrl || hit.url,
        status: hit.commitStatus || hit.status || '',
        source: hit.commitSource || '',
        repository: hit.commitRepositoryName || '',
        commitUrls: Array.isArray(hit.commitUrls) ? hit.commitUrls : [],
        pullRequestUrls: Array.isArray(hit.pullRequestUrls)
          ? hit.pullRequestUrls
          : [],
        buildUrls: Array.isArray(hit.buildUrls) ? hit.buildUrls : [],
      }));

    case 'Deployments':
      return hits.map((hit) => ({
        id: hit.deploymentId || hit.id,
        type: 'Deployments' as const,
        cardTitle: hit.deploymentName || hit.name || '',
        created:
          hit.deploymentCreated || hit.deploymentTimestamp || hit.timestamp,
        owner: hit.deploymentCreator || hit.creator || '',
        loopName: hit.loopName || '',
        application: hit.applicationName || '',
        description: hit.deploymentDescription || hit.description || '',
        url: hit.deploymentUrl || hit.url,
        environment: hit.deploymentEnvironmentName,
        startTime: hit.deploymentStartTime,
        endTime: hit.deploymentEndTime,
        triggeredBy: hit.deploymentByUser,
        version: hit.deploymentVersion,
        status:
          hit.deploymentResult || hit.deploymentStatus || hit.status || '',
        repositoryName: hit.repositoryName,
        repositoryOwner: hit.repositoryOwner,
        baseBranch: hit.baseBranch,
      }));

    default:
      return [];
  }
};

const SearchResults: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, isAuthenticated } = useUserAuth();
  const { teamspaceId: contextTeamspaceId } = useTeamspaceContext();

  // Use default teamspace ID if context doesn't provide one
  const teamspaceId = contextTeamspaceId || '';

  // OpenSearch results (executeAnalyticsSearch)
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const query = new URLSearchParams(location.search).get('q') || '';
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [selectedFilterLoop, setSelectedFilterLoop] = useState<string | null>(
    null,
  );
  const [availableFilterLoops, setAvailableFilterLoops] = useState<
    { id: string; name: string; displayName: string }[]
  >([]);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [summarizing, setSummarizing] = useState<boolean>(false);
  const [summaryExpanded, setSummaryExpanded] = useState<boolean>(false);
  const [isAiIntegrationEnabled, setIsAiIntegrationEnabled] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState<string>(
    currentPage.toString(),
  );
  const itemsPerPage = 5;
  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const activeSummaryKeyRef = useRef<string>('');
  const summaryMemoryRef = useRef<Record<string, string>>({});

  // Category-specific results (fetchIssues, fetchBuilds, etc.)
  const [categoryResults, setCategoryResults] = useState<UnifiedCardData[]>([]);
  const [categoryLoading, setCategoryLoading] = useState<boolean>(false);
  const [categoryError, setCategoryError] = useState<string>('');

  // Refined results (filtered by loop)
  const [refinedResults, setRefinedResults] = useState<UnifiedCardData[]>([]);
  const [isLoopFilterApplied, setIsLoopFilterApplied] =
    useState<boolean>(false);

  const [originalCategoryResults, setOriginalCategoryResults] = useState<
    UnifiedCardData[]
  >([]);

  // Get category and cached data from context
  const {
    cachedSearchData,
    searchTriggered,
    resetSearchTrigger,
    selectedCategory,
    searchQuery,
  } = useSearchContext();
  const { firstLoopId } = useLoopContext();
  const contextLoopId = firstLoopId || '';

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const loopService = useMemo(
    () =>
      new LoopDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  const tenantService = useMemo(
    () =>
      new TenantDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  const aiService = useMemo(
    () =>
      new AiDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
      ),
    [],
  );

  useEffect(() => {
    if (searchTriggered) {
      summaryMemoryRef.current = {};
    }
  }, [searchTriggered]);

  // Check AI integration (non-blocking)
  useEffect(() => {
    const checkAiIntegration = async () => {
      if (!contextTeamspaceId) {
        setIsAiIntegrationEnabled(false);
        return;
      }

      try {
        const response =
          await tenantService.getTeamspaceIntegrations(contextTeamspaceId);
        // A row with no isenabled flag is enabled (the server treats absence that way), so
        // only an explicit false counts as off. A bare truthy check read legacy rows as off.
        const isEnabled = response.integrations?.some(
          (integration) => integration.isenabled !== false,
        );
        setIsAiIntegrationEnabled(!!isEnabled);
      } catch (error) {
        setIsAiIntegrationEnabled(false);
      }
    };

    checkAiIntegration();
  }, [contextTeamspaceId, tenantService]);

  //Fetch OpenSearch results (executeAnalyticsSearch) - only if teamspaceId exists
  useEffect(() => {
    const fetchResults = async () => {
      // Unified guard: if no query OR no teamspace, nothing to fetch
      if (!query || !contextTeamspaceId) {
        if (results.length) setResults([]); // avoid pointless state churn
        if (loading) setLoading(false);
        return;
      }

      // OpenSearch disabled: just clear any prior error and loading state once
      if (loading) setLoading(false);
      if (error) setError(null);
    };

    fetchResults();
    // Dependencies deliberately exclude translation function 't' to prevent re-runs on locale changes
  }, [query, contextTeamspaceId, loading, error, results.length]);

  // Keep a ref to cachedSearchData so the effect can read the latest value
  // without cachedSearchData itself being a dependency (which would cause re-runs).
  const cachedSearchDataRef = useRef(cachedSearchData);
  useEffect(() => {
    cachedSearchDataRef.current = cachedSearchData;
  }, [cachedSearchData]);

  // Fetch category-specific results when search is triggered.
  // Uses searchQuery from context (set synchronously by SearchBox) rather than
  // query from URL (updated asynchronously via navigate) to avoid stale queries.
  useEffect(() => {
    if (!searchTriggered) return;

    // Use searchQuery from context — it's always up-to-date when triggerSearch fires.
    // Fall back to the URL query for cases where context hasn't been set yet.
    const activeQuery = searchQuery || query;
    if (!activeQuery) return;

    const fetchCategoryResults = async () => {
      // Immediately reset the trigger so this effect doesn't re-run
      resetSearchTrigger();

      const cached = cachedSearchDataRef.current;

      setCategoryLoading(true);
      setCategoryError('');
      setIsLoopFilterApplied(false);

      try {
        // Use cached data if it has hits for the current query and category
        if (
          cached?.hits &&
          cached.hits.length > 0 &&
          cached.query === activeQuery &&
          cached.category === selectedCategory
        ) {
          const transformedData = transformSearchResults(
            cached.hits,
            cached.category,
          );
          setCurrentPage(1);
          setCategoryResults(transformedData);
          setRefinedResults(transformedData);
          setOriginalCategoryResults(transformedData);
          setCategoryLoading(false);
          return;
        }

        // If cached data matches query and category but has empty hits, show no results
        if (
          cached?.query === activeQuery &&
          cached?.category === selectedCategory &&
          cached?.hits != null &&
          cached.hits.length === 0
        ) {
          setCategoryResults([]);
          setRefinedResults([]);
          setOriginalCategoryResults([]);
          setCurrentPage(1);
          setCategoryLoading(false);
          return;
        }

        // No usable cache — fetch from API
        setCategoryResults([]);
        setRefinedResults([]);
        setOriginalCategoryResults([]);
        setCurrentPage(1);

        if (!contextTeamspaceId) {
          setCategoryLoading(false);
          return;
        }

        const loopId = contextLoopId || '';
        let data;

        switch (selectedCategory) {
          case 'Issues':
            data = await loopService.fetchIssues(
              contextTeamspaceId,
              loopId,
              activeQuery,
            );
            break;
          case 'Builds':
            data = await loopService.fetchBuilds(
              contextTeamspaceId,
              loopId,
              activeQuery,
            );
            break;
          case 'Pull Requests':
            data = await loopService.fetchPullRequests(
              contextTeamspaceId,
              loopId,
              activeQuery,
            );
            break;
          case 'Commits':
            data = await loopService.fetchCommits(
              contextTeamspaceId,
              loopId,
              activeQuery,
            );
            break;
          case 'Deployments':
            data = await loopService.fetchDeployments(
              contextTeamspaceId,
              loopId,
              activeQuery,
            );
            break;
          default:
            data = [];
        }

        let hitsArray: any[] = [];
        if (data && data.hits && Array.isArray(data.hits)) {
          hitsArray = data.hits;
        } else if (Array.isArray(data)) {
          hitsArray = data;
        }
        const transformedData = transformSearchResults(
          hitsArray,
          selectedCategory,
        );
        setCategoryResults(transformedData);
        setRefinedResults(transformedData);
        // Cache original results for filter reset
        setOriginalCategoryResults(transformedData);
      } catch (error) {
        setCategoryError(`Failed to fetch ${selectedCategory} data`);
        setCategoryResults([]);
        setRefinedResults([]);
        setOriginalCategoryResults([]);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategoryResults();
  }, [
    searchTriggered,
    searchQuery,
    query,
    selectedCategory,
    contextTeamspaceId,
    contextLoopId,
    loopService,
    resetSearchTrigger,
  ]);

  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setPageInputValue(value);
  };

  const handlePageInputSubmit = (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement>,
  ) => {
    if ('key' in e && e.key !== 'Enter') {
      return;
    }

    const pageNumber = parseInt(pageInputValue, 10);

    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    } else {
      setPageInputValue(currentPage.toString());
    }
  };

  const displayResults = isLoopFilterApplied ? refinedResults : categoryResults;

  const totalResults = displayResults.length;
  const totalPages = Math.ceil(totalResults / itemsPerPage) || 1;
  const currentItems = useMemo(() => {
    return displayResults.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage,
    );
  }, [displayResults, currentPage]);

  useEffect(() => {
    if (currentItems.length > 0 && currentItems[0].type !== selectedCategory) {
      setSummarizing(false);
      return;
    }
    if (
      !isAiIntegrationEnabled ||
      currentItems.length === 0 ||
      !contextTeamspaceId
    ) {
      setAiSummary('');
      setSummarizing(false);
      return;
    }

    setSummarizing(true);
    aiService
      .summarizeSearchResults(
        currentItems,
        contextTeamspaceId,
        query,
        selectedCategory,
      )
      .then((summary) => {
        setAiSummary(summary);
      })
      .catch((err) => {
        setAiSummary(t('ai-summary-error'));
      })
      .finally(() => {
        setSummarizing(false);
      });
  }, [
    aiService,
    contextTeamspaceId,
    currentItems,
    isAiIntegrationEnabled,
    query,
    selectedCategory,
    t,
  ]);

  const handleFirstPage = () => setCurrentPage(1);
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const handleLastPage = () => setCurrentPage(totalPages);

  // Fetch available loops for filter dropdown
  useEffect(() => {
    const fetchFilterLoops = async () => {
      if (!isAuthenticated) return;
      try {
        const loopResponse = await loopService.getAllLoopList();
        if (loopResponse?._embedded?.loops) {
          const loops = loopResponse._embedded.loops.map((loop: any) => ({
            id: loop.id || loop.loopId || '',
            name: loop.name || '',
            displayName: loop.displayName || loop.name || 'Unnamed Loop',
          }));
          setAvailableFilterLoops(loops);
        }
      } catch {
        setAvailableFilterLoops([]);
      }
    };
    fetchFilterLoops();
  }, [isAuthenticated, loopService]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting a loop in the filter dropdown (single-select)
  const handleFilterSelect = (loopId: string) => {
    if (selectedFilterLoop === loopId) {
      // Deselect if same loop clicked again
      setSelectedFilterLoop(null);
      handleClearLoopFilter();
    } else {
      setSelectedFilterLoop(loopId);
      handleLoopFilter(loopId);
    }
  };

  // Handle removing the filter chip
  const handleRemoveFilterChip = () => {
    setSelectedFilterLoop(null);
    handleClearLoopFilter();
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSelectedFilterLoop(null);
    handleClearLoopFilter();
  };

  // Handle loop filter from RefineSearch
  const handleLoopFilter = async (loopId: string) => {
    if (!query) return;

    setCategoryLoading(true);
    setCategoryError('');

    if (
      !isLoopFilterApplied &&
      originalCategoryResults.length === 0 &&
      categoryResults.length > 0
    ) {
      setOriginalCategoryResults(categoryResults);
    }

    try {
      let data;
      const effectiveTeamspaceId = contextTeamspaceId || '';
      const effectiveLoopId = loopId || contextLoopId || '';

      switch (selectedCategory) {
        case 'Issues':
          data = await loopService.fetchIssues(
            effectiveTeamspaceId,
            effectiveLoopId,
            query,
            false,
          );
          break;
        case 'Builds':
          data = await loopService.fetchBuilds(
            effectiveTeamspaceId,
            effectiveLoopId,
            query,
            false,
          );
          break;
        case 'Pull Requests':
          data = await loopService.fetchPullRequests(
            effectiveTeamspaceId,
            effectiveLoopId,
            query,
            false,
          );
          break;
        case 'Commits':
          data = await loopService.fetchCommits(
            effectiveTeamspaceId,
            effectiveLoopId,
            query,
            false,
          );
          break;
        case 'Deployments':
          data = await loopService.fetchDeployments(
            effectiveTeamspaceId,
            effectiveLoopId,
            query,
            false,
          );
          break;
        default:
          data = [];
      }

      let hitsArray: any[] = [];
      if (data && data.hits && Array.isArray(data.hits)) {
        hitsArray = data.hits;
      } else if (Array.isArray(data)) {
        hitsArray = data;
      }

      const transformedData = transformSearchResults(
        hitsArray,
        selectedCategory,
      );
      setRefinedResults(transformedData);
      setIsLoopFilterApplied(true);
      setCurrentPage(1);
    } catch (error) {
      setCategoryError(t('error-fetching-filtered-data'));
      setRefinedResults([]);
    } finally {
      setCategoryLoading(false);
    }
  };

  // Handle clearing the loop filter - restore original cached results
  const handleClearLoopFilter = () => {
    console.info(
      '[SearchResults] Loop filter cleared - restoring cached unfiltered results',
    );
    if (originalCategoryResults.length > 0) {
      setRefinedResults(originalCategoryResults);
      setCategoryResults(originalCategoryResults);
    }
    setCategoryError('');
    setIsLoopFilterApplied(false);
    setCurrentPage(1);
  };

  return (
    <div
      id="search-results"
      className="min-h-screen bg-carbon-light font-[Roboto] dark:bg-carbon-dark"
      data-testid="search-results-container"
    >
      {/* Removed redundant 'Search' heading – breadcrumb already shows 'Search Results' */}
      {/* Inline filter toolbar bar */}
      <div
        className="mb-4 flex items-center justify-between border-b border-gray-200 bg-carbon-light px-6 py-3 dark:border-gray-700 dark:bg-carbon-darkPanel"
        id="id-search-results-toolbar"
        data-testid="search-results-toolbar"
      >
        <div
          className="flex flex-1 items-center gap-3"
          id="id-search-results-search-wrapper"
          data-testid="search-results-search-wrapper"
        >
          {/* Search Input */}
          <div
            className="flex items-center rounded border border-gray-300 bg-white dark:border-gray-600 dark:bg-carbon-darkInput"
            id="id-search-results-search-field"
            data-testid="search-results-search-field"
          >
            <span
              className="border-r border-gray-300 px-2 py-1.5 dark:border-gray-600"
              id="id-search-results-search-icon"
              data-testid="search-results-search-icon"
            >
              <Search className="size-4 text-gray-500 dark:text-gray-400" />
            </span>
            <input
              type="text"
              placeholder={t('search-placeholder')}
              value={query}
              readOnly
              className="w-96 cursor-default bg-transparent px-3 py-1.5 text-sm text-gray-700 outline-none placeholder:text-gray-400 dark:text-gray-200 dark:placeholder:text-gray-500"
            />
          </div>
        </div>

        <div
          className="flex shrink-0 items-center gap-2"
          id="id-search-results-filter-controls"
          data-testid="search-results-filter-controls"
        >
          {/* Filters Button + Dropdown */}
          <div
            className="relative"
            ref={filterDropdownRef}
            id="id-search-results-filter-dropdown"
            data-testid="search-results-filter-dropdown"
          >
            <button
              className={cn(
                'flex items-center gap-1.5 whitespace-nowrap border px-3.5 py-1.5 text-sm font-medium',
                'bg-white text-gray-900 dark:bg-carbon-darkInput dark:text-gray-100',
                'border-gray-300 dark:border-gray-600',
                'hover:bg-gray-50 dark:hover:bg-gray-700',
                'transition-colors',
                selectedFilterLoop &&
                  'border-brand-interactive text-brand-interactive dark:border-brand-interactiveDark dark:text-brand-interactiveDark',
              )}
              onClick={() => {
                setShowFilter((prev) => !prev);
              }}
            >
              {t('filters')}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={cn(
                  'transition-transform duration-200',
                  showFilter && 'rotate-180',
                )}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {showFilter && (
              <div
                className="absolute right-0 top-full z-50 mt-1 min-w-[180px] border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-carbon-darkPanel"
                id="id-search-results-filter-options"
                data-testid="search-results-filter-options"
              >
                {availableFilterLoops.map((loop) => (
                  <label
                    key={loop.id}
                    className="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2.5 text-sm text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    <input
                      type="radio"
                      name="loop-filter"
                      checked={selectedFilterLoop === loop.id}
                      onChange={() => handleFilterSelect(loop.id)}
                      className="size-4 shrink-0 cursor-pointer accent-brand-interactive dark:accent-brand-interactiveDark"
                    />
                    {loop.displayName}
                  </label>
                ))}
                {selectedFilterLoop && (
                  <button
                    className="block w-full border-t border-gray-200 px-4 py-2.5 text-left text-sm text-status-failRed hover:bg-red-50 dark:border-gray-700 dark:text-red-400 dark:hover:bg-red-950"
                    onClick={handleClearAllFilters}
                  >
                    {t('clear-all-filters')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        id="search-content-wrapper"
        className="w-full px-6 py-2.5"
        data-testid="search-results-content-wrapper"
      >
        <div
          className="flex-1"
          id="id-search-results-content"
          data-testid="search-results-content"
        >
          <div
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-carbon-darkPanel"
            id="id-search-results-card"
            data-testid="search-results-card"
          >
            {query && (
              <>
                <div
                  className="mb-6 flex items-center justify-between text-sm font-normal text-gray-600 dark:text-gray-300"
                  id="id-search-results-count-header"
                  data-testid="search-results-count-header"
                >
                  <span
                    id="id-search-results-count-text"
                    data-testid="search-results-count-text"
                  >
                    {t('showing-results-category', {
                      count: totalResults,
                      category: selectedCategory,
                    })}{' '}
                    <span
                      className="font-semibold text-gray-900 dark:text-white"
                      id="id-search-results-query-text"
                      data-testid="search-results-query-text"
                    >
                      &quot;{query}&quot;
                    </span>
                  </span>
                </div>

                {/* Filter Chip */}
                {selectedFilterLoop &&
                  (() => {
                    const loop = availableFilterLoops.find(
                      (l) => l.id === selectedFilterLoop,
                    );
                    return (
                      <div
                        className="mb-4 flex flex-wrap items-center gap-2"
                        id="id-search-results-filter-chips"
                        data-testid="search-results-filter-chips"
                      >
                        <span
                          className="whitespace-nowrap text-[13px] text-gray-500 dark:text-gray-400"
                          id="id-search-results-filters-label"
                          data-testid="search-results-filters-label"
                        >
                          {t('filters-applied')}
                        </span>
                        <span
                          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-brand-interactive bg-brand-interactive/10 px-3 py-1 text-xs font-medium text-brand-interactive dark:border-brand-interactiveDark dark:bg-brand-interactiveDark/15 dark:text-brand-chipBorder"
                          id="id-search-results-filter-chip"
                          data-testid="search-results-filter-chip"
                        >
                          {loop?.displayName || selectedFilterLoop}
                          <button
                            className="inline-flex size-3.5 items-center justify-center border-none bg-transparent p-0 text-xs leading-none text-brand-interactive hover:text-brand-interactive dark:text-brand-chipBorder dark:hover:text-white"
                            onClick={handleRemoveFilterChip}
                            aria-label={`Remove ${loop?.displayName || selectedFilterLoop}`}
                          >
                            ×
                          </button>
                        </span>
                      </div>
                    );
                  })()}

                {/* AI Summary */}
                {displayResults.length > 0 && isAiIntegrationEnabled && (
                  <>
                    <div
                      className="my-6 border-t border-gray-200 dark:border-gray-700"
                      id="id-search-results-ai-divider"
                      data-testid="search-results-ai-divider"
                    ></div>

                    {/* AI Summary Icon — full badge with text */}
                    <div
                      className="mb-6 flex items-center"
                      id="id-search-results-ai-icon"
                      data-testid="search-results-ai-icon"
                    >
                      <img
                        src={AiSummaryIcon}
                        alt={t('ai-summary')}
                        className="size-auto"
                      />
                    </div>

                    {/* Summary Content */}
                    <div
                      className="mb-6 space-y-3 text-[14px] leading-6 text-gray-700 dark:text-gray-300"
                      id="id-search-results-ai-summary"
                      data-testid="search-results-ai-summary"
                    >
                      {summarizing ? (
                        <p>{t('generating-summary')}</p>
                      ) : (
                        <div
                          className={`prose prose-sm dark:prose-invert max-w-none [&_p]:my-2 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 ${summaryExpanded ? '' : 'line-clamp-4'}`}
                          id="id-search-results-ai-markdown"
                          data-testid="search-results-ai-markdown"
                        >
                          <ReactMarkdown>
                            {aiSummary || t('no-summary-available')}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>

                    {/* Read More Button */}
                    {!summarizing && !!aiSummary && (
                      <button
                        type="button"
                        onClick={() => setSummaryExpanded((prev) => !prev)}
                        aria-expanded={summaryExpanded}
                        className="mb-8 rounded border border-carbon-linkBorder px-4 py-1.5 text-[14px] font-normal text-gray-800 transition-colors hover:bg-blue-50 dark:border-brand-interactiveDark dark:text-gray-200 dark:hover:bg-blue-950"
                      >
                        {summaryExpanded ? t('show-less') : t('read-more')}{' '}
                        {summaryExpanded ? '↑' : '→'}
                      </button>
                    )}
                  </>
                )}

                <hr className="my-6 border-t border-none border-gray-200 dark:border-gray-700" />

                {/* Results, Loading, or Error */}
                {categoryLoading ? (
                  <div
                    className="my-10 text-center"
                    id="id-search-results-loading"
                    data-testid="search-results-loading"
                  >
                    <div
                      role="progressbar"
                      className="mx-auto size-10 animate-spin rounded-full border-4 border-gray-200 border-t-brand-interactive dark:border-gray-700 dark:border-t-brand-interactiveDark"
                      id="id-search-results-spinner"
                      data-testid="search-results-spinner"
                    />
                    <p className="mt-4 text-gray-500 dark:text-gray-400">
                      {t('loading-results')}
                    </p>
                  </div>
                ) : categoryError ? (
                  <p className="mx-4 my-5 min-h-[160px] text-status-failRed dark:text-red-400">
                    {categoryError}
                  </p>
                ) : totalResults > 0 ? (
                  <>
                    <FilterCards
                      data={currentItems}
                      category={selectedCategory}
                    />

                    {/* Pagination */}
                    <div
                      className="flex items-center justify-start px-4 py-3"
                      id="id-search-results-pagination"
                      data-testid="search-results-pagination"
                    >
                      <button
                        onClick={handleFirstPage}
                        disabled={currentPage === 1}
                        className={cn(
                          'flex items-center border-none bg-transparent p-1 text-gray-500 dark:text-gray-400',
                          currentPage === 1 &&
                            'cursor-not-allowed text-gray-300 dark:text-gray-600',
                        )}
                      >
                        <PageFirst size={20} />
                      </button>
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className={cn(
                          'flex items-center border-none bg-transparent p-1 text-gray-500 dark:text-gray-400',
                          currentPage === 1 &&
                            'cursor-not-allowed text-gray-300 dark:text-gray-600',
                        )}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <span
                        className="mx-2 text-sm text-gray-500 dark:text-gray-400"
                        id="id-search-results-page-label"
                        data-testid="search-results-page-label"
                      >
                        {t('page')}
                      </span>
                      <input
                        type="text"
                        id="id-pagination-input"
                        data-testid="pagination-input"
                        value={pageInputValue}
                        onChange={handlePageInputChange}
                        onKeyDown={handlePageInputSubmit}
                        onBlur={handlePageInputSubmit}
                        className="mx-2 h-[30px] w-10 rounded border border-gray-300 bg-white text-center text-sm text-gray-900 dark:border-gray-600 dark:bg-carbon-darkInput dark:text-white"
                      />
                      <span
                        className="mx-2 text-sm text-gray-500 dark:text-gray-400"
                        id="id-search-results-total-pages"
                        data-testid="search-results-total-pages"
                      >
                        {t('of-total-pages', { totalPages })}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className={cn(
                          'flex items-center border-none bg-transparent p-1 text-gray-500 dark:text-gray-400',
                          currentPage === totalPages &&
                            'cursor-not-allowed text-gray-300 dark:text-gray-600',
                        )}
                      >
                        <ChevronRight size={20} />
                      </button>
                      <button
                        onClick={handleLastPage}
                        disabled={currentPage === totalPages}
                        className={cn(
                          'flex items-center border-none bg-transparent p-1 text-gray-500 dark:text-gray-400',
                          currentPage === totalPages &&
                            'cursor-not-allowed text-gray-300 dark:text-gray-600',
                        )}
                      >
                        <PageLast size={20} />
                      </button>
                    </div>
                  </>
                ) : (
                  <p
                    className="min-h-[170px] p-4 text-base text-gray-900 dark:text-gray-200"
                    data-testid={t('no-category-results')}
                  >
                    {t('no-results-category', { category: selectedCategory })}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
